import React, { createContext, useContext, useEffect, useState } from "react";
import { Button, Input, message, Modal, Tooltip, Upload, Spin } from "antd";
import { BugOutlined, PlusOutlined } from "@ant-design/icons";
import fetchIntercept from "fetch-intercept";
import html2canvas from "html2canvas";
import Axios from "axios";

import bug from "./assets/icons/bug.svg";
import { controller } from "./controller";

const BugReporterContext = createContext();

const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

function BugReporter({ children }) {
  const [showBugReporterModal, setShowBugReporterModal] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [isCapturing, setIsCapturing] = useState(false);
  const [consoleLogs, setConsoleLogs] = useState([]);
  const [networkRequests, setNetworkRequests] = useState([]);

  async function canvasToFile(canvas, name) {
    const dataUrl = canvas.toDataURL("image/png");
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    const file = new File([blob], name || "screenshot.png", { type: "image/png" });
    return {
      uid: Date.now().toString() + Math.random().toString(36).slice(2, 6),
      name: name || "screenshot.png",
      status: "done",
      url: dataUrl,
      originFileObj: file,
    };
  }

  // Fast auto-capture via html2canvas — can fail on modern CSS / tainted images.
  async function captureScreenShot() {
    try {
      setIsCapturing(true);
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        scale: 0.7,
        width: window.innerWidth,
        height: window.innerHeight,
        x: window.pageXOffset,
        y: window.pageYOffset,
        logging: false,
      });
      const newFile = await canvasToFile(canvas);
      setFileList((prev) => [...prev, newFile]);
    } catch (err) {
      console.error("html2canvas failed:", err);
      const detail = err && err.message ? String(err.message).slice(0, 140) : "unknown error";
      message.warning("Auto-capture failed (" + detail + "). Click 'Capture Screen' in the modal for a reliable screenshot.");
    } finally {
      setIsCapturing(false);
    }
  }

  // Reliable capture via the browser's Screen Capture API — always works, but prompts user to pick a surface.
  async function captureViaScreenShare() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
      message.error("Screen capture is not supported in this browser");
      return;
    }
    let stream;
    try {
      setIsCapturing(true);
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: "browser" },
        preferCurrentTab: true,
        selfBrowserSurface: "include",
        audio: false,
      });
      const track = stream.getVideoTracks()[0];

      let canvas;
      if (window.ImageCapture) {
        const imageCapture = new window.ImageCapture(track);
        const bitmap = await imageCapture.grabFrame();
        canvas = document.createElement("canvas");
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        canvas.getContext("2d").drawImage(bitmap, 0, 0);
      } else {
        const video = document.createElement("video");
        video.srcObject = stream;
        video.muted = true;
        await video.play();
        await new Promise((r) => setTimeout(r, 120));
        canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext("2d").drawImage(video, 0, 0);
        video.pause();
      }

      const newFile = await canvasToFile(canvas, "screen-capture.png");
      setFileList((prev) => [...prev, newFile]);
      message.success("Screen captured");
    } catch (err) {
      console.error("Screen capture failed:", err);
      if (err && err.name === "NotAllowedError") {
        message.info("Screen capture cancelled");
      } else {
        const detail = err && err.message ? String(err.message).slice(0, 140) : "unknown error";
        message.error("Screen capture failed: " + detail);
      }
    } finally {
      if (stream) stream.getTracks().forEach((t) => t.stop());
      setIsCapturing(false);
    }
  }

  useEffect(() => {
    let mounted = true; // Flag to track if component is mounted
    let lastRequest = null;

    const unregister = fetchIntercept.register({
      request: function (url, config) {
        lastRequest = { url, config };
        return [url, config];
      },
      requestError: function (error) {
        if (mounted) {
          // Only update state if mounted
          setNetworkRequests((prev) =>
            [
              ...prev,
              {
                timestamp: new Date().toISOString(),
                url: lastRequest ? lastRequest.url : "UNKNOWN",
                method: lastRequest ? lastRequest.config && lastRequest.config.method : "UNKNOWN",
                requestPayload: lastRequest ? lastRequest.config && lastRequest.config.body : "UNKNOWN",
                error: error.message,
              },
            ].slice(-10)
          );
        }
        return Promise.reject(error);
      },
      response: function (response) {
        const clonedResponse = response.clone();
        clonedResponse.json().then(
          (data) => {
            if (mounted) {
              // Only update state if mounted
              setNetworkRequests((prev) =>
                [
                  ...prev,
                  {
                    timestamp: new Date().toISOString(),
                    url: response.url,
                    method: lastRequest ? lastRequest.config && lastRequest.config.method : "GET",
                    requestPayload: lastRequest ? lastRequest.config && lastRequest.config.body : null,
                    responseStatus: response.status,
                    responseData: data,
                  },
                ].slice(-10)
              );
            }
          },
          () => {
            if (mounted) {
              // Only update state if mounted
              setNetworkRequests((prev) =>
                [
                  ...prev,
                  {
                    timestamp: new Date().toISOString(),
                    url: response.url,
                    method: lastRequest ? lastRequest.config && lastRequest.config.method : "GET",
                    requestPayload: lastRequest ? lastRequest.config && lastRequest.config.body : null,
                    responseStatus: response.status,
                    responseData: null,
                  },
                ].slice(-10)
              );
            }
          }
        );
        return response;
      },
      responseError: function (error) {
        if (mounted) {
          // Only update state if mounted
          setNetworkRequests((prev) =>
            [
              ...prev,
              {
                timestamp: new Date().toISOString(),
                url: error.url || "UNKNOWN",
                method: "UNKNOWN",
                requestPayload: null,
                error: error.message,
              },
            ].slice(-10)
          );
        }
        return Promise.reject(error);
      },
    });

    const axiosReqInterceptor = Axios.interceptors.request.use(
      function (config) {
        config.metadata = { startedAt: new Date().toISOString() };
        return config;
      },
      function (error) { return Promise.reject(error); }
    );

    const axiosResInterceptor = Axios.interceptors.response.use(
      function (response) {
        if (mounted) {
          setNetworkRequests((prev) =>
            [
              ...prev,
              {
                timestamp: (response.config && response.config.metadata && response.config.metadata.startedAt) || new Date().toISOString(),
                url: (response.config && response.config.url) || "UNKNOWN",
                method: (response.config && response.config.method && response.config.method.toUpperCase()) || "GET",
                requestPayload: response.config && response.config.data ? String(response.config.data).slice(0, 500) : null,
                responseStatus: response.status,
                responseData: null,
                transport: "axios",
              },
            ].slice(-10)
          );
        }
        return response;
      },
      function (error) {
        if (mounted) {
          const cfg = (error && error.config) || {};
          setNetworkRequests((prev) =>
            [
              ...prev,
              {
                timestamp: (cfg.metadata && cfg.metadata.startedAt) || new Date().toISOString(),
                url: cfg.url || "UNKNOWN",
                method: (cfg.method && cfg.method.toUpperCase()) || "UNKNOWN",
                requestPayload: cfg.data ? String(cfg.data).slice(0, 500) : null,
                responseStatus: error.response ? error.response.status : null,
                error: error.message,
                transport: "axios",
              },
            ].slice(-10)
          );
        }
        return Promise.reject(error);
      }
    );

    return () => {
      mounted = false; // Set flag to false on cleanup
      Axios.interceptors.request.eject(axiosReqInterceptor);
      Axios.interceptors.response.eject(axiosResInterceptor);
      unregister();
    };
  }, []);

  // Capture console logs
  useEffect(() => {
    const originalConsole = {
      log: console.log,
      error: console.error,
      warn: console.warn,
      info: console.info,
    };

    const captureLog = (type, args) => {
      // Capture the stack trace, skipping the wrapper itself
      const err = new Error();
      Error.captureStackTrace(err, captureLog); // Or use arguments.callee if needed, but it's deprecated in strict mode
      const stack = err.stack.split("\n").slice(2).join("\n"); // Slice to remove Error line and wrapper line

      setConsoleLogs((prev) =>
        [
          ...prev,
          {
            type,
            timestamp: new Date().toISOString(),
            args: Array.from(args),
            callerStack: stack, // Store the cleaned stack for origin info
          },
        ].slice(-100)
      ); // Keep last 100 logs
    };

    console.log = function () {
      captureLog("log", arguments);
      originalConsole.log.apply(console, arguments);
    };
    console.error = function () {
      captureLog("error", arguments);
      originalConsole.error.apply(console, arguments);
    };
    console.warn = function () {
      captureLog("warn", arguments);
      originalConsole.warn.apply(console, arguments);
    };
    console.info = function () {
      captureLog("info", arguments);
      originalConsole.info.apply(console, arguments);
    };

    return () => {
      console.log = originalConsole.log;
      console.error = originalConsole.error;
      console.warn = originalConsole.warn;
      console.info = originalConsole.info;
    };
  }, []);

  return (
    <BugReporterContext.Provider
      value={{
        showBugReporterModal,
        setShowBugReporterModal,
        fileList,
        setFileList,
        isCapturing,
        captureScreenShot,
        captureViaScreenShare,
        consoleLogs,
        networkRequests,
      }}
    >
      {children}
    </BugReporterContext.Provider>
  );
}

function BugReporterModal() {
  const { showBugReporterModal, setShowBugReporterModal, fileList, setFileList, consoleLogs, networkRequests, captureViaScreenShare, isCapturing } = useContext(BugReporterContext);
  const [currentImage, setCurrentImage] = useState(undefined);
  const [loadingImage, setLoadingImage] = useState(false);
  const [bugDescription, setBugDescription] = useState("");
  const [isSendingReport, setIsSendingReport] = useState(false);

  // Update currentImage when fileList changes
  useEffect(
    function () {
      async function showLastImage() {
        try {
          setLoadingImage(true);

          if (fileList.length > 0) {
            const lastFile = fileList[fileList.length - 1];

            let imageSrc = lastFile.url || lastFile.preview;
            if (!imageSrc && lastFile.originFileObj) {
              imageSrc = await getBase64(lastFile.originFileObj);
            }

            setCurrentImage(imageSrc);
          }
        } catch (err) {
          setCurrentImage(null); // Reset when fileList is empty
          message.error("Something went wrong while loading image");
          console.error(err);
        } finally {
          setLoadingImage(false);
        }
      }
      showLastImage();
    },
    [fileList]
  );

  // Check file type before upload
  const beforeUpload = (file) => {
    const allowedType = ["image/png", "image/jpg", "image/jpeg"];
    if (!allowedType.includes(file.type)) {
      message.error("Please upload only images files");
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  async function handlePreview(file) {
    let imageSrc = file.url || file.preview;
    if (!imageSrc && file.originFileObj) {
      imageSrc = await getBase64(file.originFileObj);
      setFileList((prev) => prev.map((item) => (item.uid === file.uid ? { ...item, preview: imageSrc } : item)));
    }
    setCurrentImage(imageSrc);
  }

  // Send the bug to the backend
  async function sendReport() {
    if (!bugDescription.trim() && fileList.length === 0) {
      message.warning("Please describe the issue or attach a screenshot before submitting.");
      return;
    }

    try {
      setIsSendingReport(true);

      let officeId = null;
      try {
        const stored = localStorage.getItem("officeLogo");
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed && parsed.id != null) officeId = parsed.id;
        }
      } catch (e) {
        console.warn("Could not read officeLogo from localStorage:", e);
      }

      let bugReportData = new FormData();
      bugReportData.append("console_logs", JSON.stringify(consoleLogs));
      bugReportData.append("endpoint_logs", JSON.stringify(networkRequests));
      bugReportData.append("url", window.location.href);
      if (officeId != null) bugReportData.append("office", officeId);
      bugReportData.append("description", bugDescription);
      fileList.forEach((file) => {
        if (file.originFileObj) bugReportData.append("screenshots", file.originFileObj);
      });

      const response = await controller.sendBugReport(bugReportData);

      if (response.status < 250) {
        message.success("Bug reported successfully");
        setShowBugReporterModal(false);
        setFileList([]);
        setBugDescription("");
      } else {
        const detail = response.json && (response.json.error || JSON.stringify(response.json));
        message.error("Failed to submit bug report" + (detail ? ": " + String(detail).slice(0, 140) : ""));
      }
    } catch (err) {
      message.error("Something went wrong while reporting bug: " + (err && err.message ? err.message : "unknown error"));
      console.error(err);
    } finally {
      setIsSendingReport(false);
    }
  }

  return (
    <Modal
      open={showBugReporterModal}
      onCancel={() => {
        setShowBugReporterModal(false);
        setFileList([]);
      }}
      width="60%"
      title="Report a Bug"
      footer={() => (
        <div style={{ marginTop: 20, display: "flex", alignItems: "center", justifySelf: "end", gap: 10 }}>
          <Button
            key="cancel"
            onClick={() => {
              setShowBugReporterModal(false);
              setFileList([]);
            }}
            disabled={isSendingReport}
          >
            Cancel
          </Button>
          <Button key="submit" type="primary" onClick={sendReport} loading={isSendingReport} disabled={isSendingReport}>
            Submit
          </Button>
        </div>
      )}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 20,
          border: "1px solid #6b43b5",
          borderRadius: 5,
          boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.15)",
          height: 400,
        }}
      >
        {loadingImage ? <Spin /> : <img src={currentImage} alt="preview" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
        <Button onClick={captureViaScreenShare} loading={isCapturing}>
          Capture Screen
        </Button>
        <span style={{ color: "#848696", fontSize: 12 }}>
          Uses your browser's screen-share prompt for a reliable screenshot of this tab.
        </span>
      </div>

      <div>
        <Upload
          name="file"
          multiple={false}
          fileList={fileList}
          showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
          beforeUpload={beforeUpload}
          onChange={(info) => setFileList(info.fileList)}
          onPreview={handlePreview}
          listType="picture-card"
          style={{ backgroundColor: "#ffffff", padding: "30px 10px", marginBottom: 30 }}
        >
          <Button style={{ border: 0, background: "none", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <PlusOutlined />
            <div style={{ marginTop: 8 }}>Upload</div>
          </Button>
        </Upload>
      </div>

      <div>
        <p htmlFor="bug-description">Describe the issue:</p>
        <Input.TextArea
          placeholder="Describe the issue..."
          style={{ width: "100%", borderRadius: 5, height: 100 }}
          onChange={(e) => setBugDescription(e.target.value)}
          value={bugDescription}
        />
      </div>

      <div
        style={{
          marginTop: 12,
          padding: "8px 12px",
          background: "#f8f6fc",
          border: "1px solid #e8e4f0",
          borderRadius: 6,
          fontSize: 12,
          color: "#848696",
          display: "flex",
          flexWrap: "wrap",
          gap: "4px 10px",
        }}
      >
        <span>Also attached automatically:</span>
        <span style={{ color: "#6b43b5", fontWeight: 500 }}>page URL</span>
        <span>·</span>
        <span style={{ color: "#6b43b5", fontWeight: 500 }}>office ID</span>
        <span>·</span>
        <span style={{ color: "#6b43b5", fontWeight: 500 }}>{consoleLogs.length} console log{consoleLogs.length === 1 ? "" : "s"}</span>
        <span>·</span>
        <span style={{ color: "#6b43b5", fontWeight: 500 }}>{networkRequests.length} recent API call{networkRequests.length === 1 ? "" : "s"}</span>
      </div>
    </Modal>
  );
}

function BugReporterButton() {
  const { setShowBugReporterModal, captureScreenShot, isCapturing } = useContext(BugReporterContext);

  async function handleClick() {
    await captureScreenShot();
    setShowBugReporterModal(true);
  }

  return (
    <Button
      onClick={handleClick}
      style={{ position: "fixed", bottom: 20, right: 80, display: "flex", alignItems: "center", gap: 10 }}
      type="primary"
      loading={isCapturing}
    >
      <img src={bug} alt="bug" />
      Report Bug
    </Button>
  );
}

function BugReporterHeaderIcon() {
  const { setShowBugReporterModal, captureScreenShot, isCapturing } = useContext(BugReporterContext);

  function handleClick() {
    setShowBugReporterModal(true);
    captureScreenShot();
  }

  return (
    <Tooltip title="Report a bug" placement="bottom">
      <button
        type="button"
        onClick={handleClick}
        disabled={isCapturing}
        aria-label="Report a bug"
        className="topbar-icon-btn"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          verticalAlign: "middle",
          width: 40,
          height: 40,
          padding: 0,
          background: "#EEEDFA",
          border: "none",
          borderRadius: 8,
          cursor: isCapturing ? "wait" : "pointer",
          opacity: isCapturing ? 0.6 : 1,
          color: "#6B43B5",
          lineHeight: 1,
        }}
      >
        {isCapturing ? (
          <Spin size="small" />
        ) : (
          <BugOutlined style={{ fontSize: 20, color: "#6B43B5" }} />
        )}
      </button>
    </Tooltip>
  );
}

// Trigger the bug reporter from anywhere inside the <BugReporter> provider
// (e.g. a sidebar menu item) — mirrors the header icon's behaviour.
export function useBugReporter() {
  const ctx = useContext(BugReporterContext);
  return {
    openBugReporter: () => {
      if (!ctx) return;
      ctx.setShowBugReporterModal(true);
      ctx.captureScreenShot();
    },
  };
}

BugReporter.Modal = BugReporterModal;
BugReporter.Button = BugReporterButton;
BugReporter.HeaderIcon = BugReporterHeaderIcon;

export default BugReporter;
